/**
 * Influencer Marketing Agent — 达人营销Agent
 *
 * 4-Tab结构:
 *  - 发现达人 (InfluencerSearch)
 *  - 邀约邮件 (EmailGenerator)
 *  - 合作管理 (CampaignList)
 *  - 效果分析 (InfluencerStats)
 *
 * 设计风格: 暗夜蓝 + 萤火暖金，内联样式 + CSS变量
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Tabs, Select, Input, Button, Card, Avatar, Tag, Row, Col,
  Table, Progress, message, Spin, Form, InputNumber, Descriptions,
  Typography, Space, Statistic, Divider,
} from 'antd';
import {
  SearchOutlined, SendOutlined, TeamOutlined, BarChartOutlined,
  MailOutlined, EyeOutlined, CrownOutlined, StarOutlined,
  AimOutlined, WalletOutlined, RiseOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import {
  influencerAPI, INFLUENCER_STATUS, INFLUENCER_STATUS_COLOR,
} from '../../api/influencer';

const { Title, Text } = Typography;
const { TextArea } = Input;

// ─── 颜色常量 ───
const C = {
  bg: '#0a0f1e',
  cardBg: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  accent: '#6366f1',
  accentSoft: 'rgba(99,102,241,0.15)',
  gold: '#E8C85A',
  text: 'rgba(240,245,250,0.95)',
  textSec: 'rgba(240,245,250,0.6)',
  textMute: 'rgba(240,245,250,0.35)',
  green: '#22c55e',
};

// ─── 工具函数 ───
const formatFollowers = (n) => {
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
};

// ========================================================
// Tab 1: 发现达人 — InfluencerSearch
// ========================================================
const InfluencerSearch = ({ onSelectInfluencer }) => {
  const [loading, setLoading] = useState(false);
  const [influencers, setInfluencers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filters, setFilters] = useState({
    category: undefined,
    region: undefined,
    minFollowers: undefined,
    maxFollowers: undefined,
    minEngagement: undefined,
    keyword: '',
  });

  // 初始化
  useEffect(() => {
    influencerAPI.getCategories().then(setCategories);
    influencerAPI.getRegions().then(setRegions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await influencerAPI.search(filters);
      setInfluencers(data);
    } catch {
      message.error('搜索失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleContact = (inf) => {
    message.success(`已选择达人: ${inf.name}`);
    onSelectInfluencer?.(inf);
  };

  return (
    <div>
      {/* 搜索表单 */}
      <Card
        style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 20 }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={5}>
            <Text style={{ color: C.textSec, fontSize: 12, marginBottom: 4, display: 'block' }}>品类</Text>
            <Select
              placeholder="全部品类"
              allowClear
              style={{ width: '100%' }}
              value={filters.category}
              onChange={(v) => setFilters((p) => ({ ...p, category: v }))}
              options={categories.map((c) => ({ value: c, label: c }))}
            />
          </Col>
          <Col xs={24} sm={8} md={5}>
            <Text style={{ color: C.textSec, fontSize: 12, marginBottom: 4, display: 'block' }}>地区</Text>
            <Select
              placeholder="全部地区"
              allowClear
              style={{ width: '100%' }}
              value={filters.region}
              onChange={(v) => setFilters((p) => ({ ...p, region: v }))}
              options={regions.map((r) => ({ value: r, label: r }))}
            />
          </Col>
          <Col xs={24} sm={8} md={5}>
            <Text style={{ color: C.textSec, fontSize: 12, marginBottom: 4, display: 'block' }}>最小粉丝量(万)</Text>
            <InputNumber
              min={0}
              max={5000}
              placeholder="不限"
              style={{ width: '100%' }}
              value={filters.minFollowers ? filters.minFollowers / 10000 : undefined}
              onChange={(v) => setFilters((p) => ({ ...p, minFollowers: v ? v * 10000 : undefined }))}
            />
          </Col>
          <Col xs={24} sm={8} md={5}>
            <Text style={{ color: C.textSec, fontSize: 12, marginBottom: 4, display: 'block' }}>最小互动率(%)</Text>
            <InputNumber
              min={0}
              max={20}
              step={0.5}
              placeholder="不限"
              style={{ width: '100%' }}
              value={filters.minEngagement}
              onChange={(v) => setFilters((p) => ({ ...p, minEngagement: v }))}
            />
          </Col>
          <Col xs={24} md={4}>
            <Text style={{ color: C.textSec, fontSize: 12, marginBottom: 4, display: 'block' }}>关键词</Text>
            <Input
              placeholder="搜索达人/标签"
              allowClear
              value={filters.keyword}
              onChange={(e) => setFilters((p) => ({ ...p, keyword: e.target.value }))}
            />
          </Col>
          <Col xs={24} md={24} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              搜索达人
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => setFilters({ category: undefined, region: undefined, minFollowers: undefined, maxFollowers: undefined, minEngagement: undefined, keyword: '' })}
            >
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 达人卡片列表 */}
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {influencers.map((inf) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={inf.id}>
              <Card
                hoverable
                style={{
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  height: '100%',
                }}
              >
                <div style={{ padding: 20, textAlign: 'center' }}>
                  <Avatar size={64} src={inf.avatar} style={{ marginBottom: 12 }} />
                  <Title level={5} style={{ color: C.text, margin: '0 0 4px 0' }}>{inf.name}</Title>
                  <Tag color={INFLUENCER_STATUS_COLOR[inf.status]} style={{ fontSize: 11 }}>
                    {INFLUENCER_STATUS[inf.status]}
                  </Tag>
                </div>

                <Divider style={{ margin: '0 0 12px 0', borderColor: C.border }} />

                <div style={{ padding: '0 20px 16px' }}>
                  <Row gutter={8} style={{ marginBottom: 10 }}>
                    <Col span={12}>
                      <Statistic
                        title={<span style={{ color: C.textMute, fontSize: 11 }}>粉丝数</span>}
                        value={formatFollowers(inf.followers)}
                        valueStyle={{ color: C.gold, fontSize: 16, fontWeight: 600 }}
                        prefix={<TeamOutlined style={{ fontSize: 12 }} />}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={<span style={{ color: C.textMute, fontSize: 11 }}>互动率</span>}
                        value={inf.engagementRate}
                        suffix="%"
                        valueStyle={{ color: C.green, fontSize: 16, fontWeight: 600 }}
                        prefix={<RiseOutlined style={{ fontSize: 12 }} />}
                      />
                    </Col>
                  </Row>

                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ color: C.textSec, fontSize: 12 }}>地区: </Text>
                    <Text style={{ color: C.text, fontSize: 12 }}>{inf.region}</Text>
                    <Text style={{ color: C.textSec, fontSize: 12, marginLeft: 12 }}>品类: </Text>
                    <Text style={{ color: C.text, fontSize: 12 }}>{inf.category}</Text>
                  </div>

                  <div style={{ marginBottom: 14, minHeight: 22 }}>
                    {inf.tags.map((tag) => (
                      <Tag key={tag} style={{ fontSize: 11, color: C.accent, background: C.accentSoft, border: 'none' }}>
                        #{tag}
                      </Tag>
                    ))}
                  </div>

                  <Button
                    type="primary"
                    block
                    icon={<SendOutlined />}
                    onClick={() => handleContact(inf)}
                  >
                    联系TA
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {!loading && influencers.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: C.textMute }}>
            <EyeOutlined style={{ fontSize: 36, marginBottom: 12, display: 'block' }} />
            没有找到匹配的达人
          </div>
        )}
      </Spin>
    </div>
  );
};

// ========================================================
// Tab 2: 邀约邮件 — EmailGenerator
// ========================================================
const EmailGenerator = ({ selectedInfluencer }) => {
  const [influencers, setInfluencers] = useState([]);
  const [products] = useState(['清透精华水', '智能手表Pro', '鲜味酱油', '夏装连衣裙', '户外背包', '联名手办']);
  const [selectedInf, setSelectedInf] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(undefined);
  const [tone, setTone] = useState('friendly');
  const [emailContent, setEmailContent] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    influencerAPI.search({}).then((data) => {
      setInfluencers(data);
      if (selectedInfluencer) {
        setSelectedInf(selectedInfluencer);
      }
    });
  }, [selectedInfluencer]);

  const handleGenerate = async () => {
    if (!selectedInf) { message.warning('请先选择达人'); return; }
    if (!selectedProduct) { message.warning('请选择产品'); return; }
    setGenerating(true);
    try {
      const result = await influencerAPI.generateEmail(selectedInf.id, selectedProduct, tone);
      setEmailContent(result.content);
      message.success('邮件已生成');
    } catch {
      message.error('生成失败');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!selectedInf) { message.warning('请先选择达人'); return; }
    if (!emailContent) { message.warning('请先生成邮件'); return; }
    setSending(true);
    try {
      await influencerAPI.sendEmail(selectedInf.id);
      message.success(`邮件已成功发送给 ${selectedInf.name}`);
    } catch {
      message.error('发送失败');
    } finally {
      setSending(false);
    }
  };

  return (
    <Row gutter={20}>
      {/* 左侧配置 */}
      <Col xs={24} md={10}>
        <Card
          title={<span style={{ color: C.text }}><MailOutlined style={{ marginRight: 8 }} />邀约配置</span>}
          style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}
        >
          <Form layout="vertical" style={{ marginTop: 8 }}>
            <Form.Item label={<span style={{ color: C.textSec }}>选择达人</span>}>
              <Select
                placeholder="请选择达人"
                value={selectedInf?.id}
                onChange={(v) => setSelectedInf(influencers.find((i) => i.id === v))}
                options={influencers.map((i) => ({
                  value: i.id,
                  label: (
                    <Space>
                      <Avatar size={24} src={i.avatar} />
                      <span>{i.name}</span>
                      <Tag style={{ fontSize: 10 }}>{i.category}</Tag>
                    </Space>
                  ),
                }))}
              />
            </Form.Item>

            <Form.Item label={<span style={{ color: C.textSec }}>推广产品</span>}>
              <Select
                placeholder="请选择产品"
                value={selectedProduct}
                onChange={setSelectedProduct}
                options={products.map((p) => ({ value: p, label: p }))}
              />
            </Form.Item>

            <Form.Item label={<span style={{ color: C.textSec }}>邮件语气</span>}>
              <Select
                value={tone}
                onChange={setTone}
                options={[
                  { value: 'formal', label: '正式' },
                  { value: 'friendly', label: '友好' },
                  { value: 'creative', label: '创意' },
                ]}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                block
                icon={<MailOutlined />}
                onClick={handleGenerate}
                loading={generating}
              >
                生成邀约邮件
              </Button>
            </Form.Item>
          </Form>

          {selectedInf && (
            <div style={{ marginTop: 16, padding: 16, background: 'rgba(99,102,241,0.06)', borderRadius: 10, border: `1px solid ${C.border}` }}>
              <Text style={{ color: C.textSec, fontSize: 12 }}>已选达人信息</Text>
              <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
                <Descriptions.Item label={<span style={{ color: C.textMute }}>姓名</span>}>
                  <span style={{ color: C.text }}>{selectedInf.name}</span>
                </Descriptions.Item>
                <Descriptions.Item label={<span style={{ color: C.textMute }}>粉丝</span>}>
                  <span style={{ color: C.gold }}>{formatFollowers(selectedInf.followers)}</span>
                </Descriptions.Item>
                <Descriptions.Item label={<span style={{ color: C.textMute }}>互动率</span>}>
                  <span style={{ color: C.green }}>{selectedInf.engagementRate}%</span>
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
        </Card>
      </Col>

      {/* 右侧邮件预览 */}
      <Col xs={24} md={14}>
        <Card
          title={<span style={{ color: C.text }}><EyeOutlined style={{ marginRight: 8 }} />邮件预览</span>}
          style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}
          extra={
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={sending}
              disabled={!emailContent}
            >
              发送邮件
            </Button>
          }
        >
          <TextArea
            value={emailContent}
            readOnly
            placeholder="点击「生成邀约邮件」按钮生成内容..."
            style={{
              minHeight: 400,
              background: 'rgba(0,0,0,0.25)',
              color: C.text,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              fontFamily: 'var(--font-sans)',
              lineHeight: 1.8,
              resize: 'vertical',
            }}
          />
        </Card>
      </Col>
    </Row>
  );
};

// ========================================================
// Tab 3: 合作管理 — CampaignList
// ========================================================
const CampaignList = () => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    setLoading(true);
    influencerAPI.getCampaigns().then((data) => {
      setCampaigns(data);
      setLoading(false);
    });
  }, []);

  const columns = [
    { title: '达人', dataIndex: 'influencerName', key: 'influencerName',
      render: (t, r) => (
        <Space>
          <Avatar size={28} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${r.influencerName}`} />
          <span style={{ color: C.text }}>{t}</span>
        </Space>
      ),
    },
    { title: '产品', dataIndex: 'product', key: 'product',
      render: (t) => <span style={{ color: C.textSec }}>{t}</span>,
    },
    { title: '状态', dataIndex: 'status', key: 'status',
      render: (s) => <Tag color={INFLUENCER_STATUS_COLOR[s]}>{INFLUENCER_STATUS[s]}</Tag>,
    },
    { title: '合作期限', key: 'period',
      render: (_, r) => (
        <span style={{ color: C.textSec, fontSize: 12 }}>
          {r.startDate} ~ {r.endDate}
        </span>
      ),
    },
    { title: '费用(¥)', dataIndex: 'fee', key: 'fee',
      render: (v) => <span style={{ color: C.gold, fontWeight: 600 }}>¥{v.toLocaleString()}</span>,
      sorter: (a, b) => a.fee - b.fee,
    },
    { title: '曝光量', dataIndex: 'impressions', key: 'impressions',
      render: (v) => <span style={{ color: C.textSec }}>{v > 0 ? (v / 10000).toFixed(1) + '万' : '-'}</span>,
    },
    { title: '转化数', dataIndex: 'conversions', key: 'conversions',
      render: (v) => <span style={{ color: C.green, fontWeight: 500 }}>{v || '-'}</span>,
    },
    {
      title: '操作', key: 'action',
      render: () => (
        <Space>
          <Button size="small" type="link" style={{ color: C.accent }}>详情</Button>
          <Button size="small" type="link" style={{ color: C.gold }}>编辑</Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<span style={{ color: C.text }}><AimOutlined style={{ marginRight: 8 }} />合作活动列表</span>}
      style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}
      extra={
        <Space>
          <Tag color="blue">{campaigns.length} 个活动</Tag>
          <Tag color="green">{campaigns.filter((c) => c.status === 3).length} 进行中</Tag>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={campaigns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </Card>
  );
};

// ========================================================
// Tab 4: 效果分析 — InfluencerStats
// ========================================================
const InfluencerStats = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setLoading(true);
    influencerAPI.getStats().then((data) => {
      setStats(data);
      setLoading(false);
    });
  }, []);

  const trendOption = stats ? {
    tooltip: { trigger: 'axis', backgroundColor: 'rgba(10,15,30,0.9)', borderColor: C.border, textStyle: { color: C.text } },
    grid: { left: '3%', right: '3%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: stats.monthlyCampaigns.map((m) => m.month),
      axisLine: { lineStyle: { color: C.border } },
      axisLabel: { color: C.textSec },
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: C.border } },
      axisLabel: { color: C.textSec },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.04)' } },
    },
    series: [{
      name: '合作数量',
      type: 'bar',
      data: stats.monthlyCampaigns.map((m) => m.count),
      itemStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: '#6366f1' },
            { offset: 1, color: '#8b5cf6' },
          ],
        },
        borderRadius: [6, 6, 0, 0],
      },
      barWidth: '50%',
    }],
  } : null;

  const pieOption = stats ? {
    tooltip: { trigger: 'item', backgroundColor: 'rgba(10,15,30,0.9)', borderColor: C.border, textStyle: { color: C.text } },
    legend: {
      orient: 'vertical',
      right: 10,
      top: 'center',
      textStyle: { color: C.textSec, fontSize: 12 },
    },
    series: [{
      name: '互动率分布',
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['40%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 8, borderColor: C.bg, borderWidth: 2 },
      label: { show: false },
      emphasis: {
        label: { show: true, fontSize: 14, fontWeight: 'bold', color: C.text },
      },
      data: stats.engagementDistribution.map((d) => ({
        name: d.name,
        value: d.value,
      })),
      color: ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#E8C85A'],
    }],
  } : null;

  return (
    <Spin spinning={loading}>
      {/* 概览统计 */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={6}>
            <Card style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              <Statistic
                title={<span style={{ color: C.textSec, fontSize: 12 }}>总合作数</span>}
                value={stats.monthlyCampaigns.reduce((s, m) => s + m.count, 0)}
                prefix={<TeamOutlined style={{ color: C.accent }} />}
                valueStyle={{ color: C.text, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              <Statistic
                title={<span style={{ color: C.textSec, fontSize: 12 }}>总投放(¥)</span>}
                value={stats.totalSpent}
                prefix={<WalletOutlined style={{ color: C.gold }} />}
                valueStyle={{ color: C.gold, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              <Statistic
                title={<span style={{ color: C.textSec, fontSize: 12 }}>平均ROI</span>}
                value={stats.totalROI}
                suffix="x"
                prefix={<RiseOutlined style={{ color: C.green }} />}
                valueStyle={{ color: C.green, fontWeight: 700 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}>
              <Statistic
                title={<span style={{ color: C.textSec, fontSize: 12 }}>平均互动率</span>}
                value={stats.avgEngagement}
                suffix="%"
                prefix={<StarOutlined style={{ color: C.gold }} />}
                valueStyle={{ color: C.text, fontWeight: 700 }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 图表 */}
      {stats && (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <Card
              title={<span style={{ color: C.text }}><BarChartOutlined style={{ marginRight: 8 }} />合作数量趋势</span>}
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}
            >
              <ReactECharts option={trendOption} style={{ height: 360 }} />
            </Card>
          </Col>
          <Col xs={24} md={10}>
            <Card
              title={<span style={{ color: C.text }}><StarOutlined style={{ marginRight: 8 }} />互动率分布</span>}
              style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 14 }}
            >
              <ReactECharts option={pieOption} style={{ height: 360 }} />
            </Card>
          </Col>
        </Row>
      )}
    </Spin>
  );
};

// ========================================================
// 主入口: InfluencerPage
// ========================================================
const InfluencerPage = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  const tabItems = [
    {
      key: 'search',
      label: (
        <span>
          <SearchOutlined />
          发现达人
        </span>
      ),
      children: <InfluencerSearch onSelectInfluencer={(inf) => { setSelectedInfluencer(inf); setActiveTab('email'); }} />,
    },
    {
      key: 'email',
      label: (
        <span>
          <MailOutlined />
          邀约邮件
        </span>
      ),
      children: <EmailGenerator selectedInfluencer={selectedInfluencer} />,
    },
    {
      key: 'campaigns',
      label: (
        <span>
          <AimOutlined />
          合作管理
        </span>
      ),
      children: <CampaignList />,
    },
    {
      key: 'stats',
      label: (
        <span>
          <BarChartOutlined />
          效果分析
        </span>
      ),
      children: <InfluencerStats />,
    },
  ];

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 32px' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ color: C.text, margin: 0 }}>
          <CrownOutlined style={{ color: C.gold, marginRight: 10 }} />
          达人营销 Agent
        </Title>
        <Text style={{ color: C.textSec, fontSize: 14 }}>
          智能发现达人 · 自动邀约邮件 · 数据驱动合作决策
        </Text>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        tabBarStyle={{ marginBottom: 0 }}
      />
    </div>
  );
};

export default InfluencerPage;
